import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	HeadContentSymbol,
	DomReadySymbol,
	SiteFeatureConfigSymbol,
	PageFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import type { IAppWillMountHandler, IPageWillMountHandler } from '@wix/thunderbolt-symbols'
import type { CustomCssPageConfig, CustomCssSiteConfig, ICustomCssAPI } from './types'
import { CustomCssAPISymbol, name } from './symbols'
import { customCssId, GLOBAL_CSS_FILE_PATH } from './constants'

export const wrapInStyleElement = (css: string, id = customCssId) => {
	return `<style id=${id}>${css}</style>`
}

export const ServerAppCustomCss = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(SiteFeatureConfigSymbol, name),
		HeadContentSymbol,
		CustomCssAPISymbol,
	] as const,
	(
		pageConfig: CustomCssPageConfig,
		siteConfig: CustomCssSiteConfig,
		head,
		customCssAPI: ICustomCssAPI
	): IPageWillMountHandler => {
		return {
			name: 'ServerAppsCustomCss',
			pageWillMount: async () => {
				const cssToInject = await customCssAPI.updateAppsCustomCss(pageConfig.appsInPage, siteConfig.appsWithCustomCss)
				if (cssToInject) {
					for (const [appDefId, css] of Object.entries(cssToInject)) {
						const cssElementId = `custom-css-${appDefId}`
						if (css) {
							head.addCustomCss!(wrapInStyleElement(css, cssElementId))
						}
					}
				}
			},
		}
	}
)

export const ServerCustomCss = withDependencies(
	[named(SiteFeatureConfigSymbol, name), HeadContentSymbol, CustomCssAPISymbol] as const,
	(siteFeatureConfig: CustomCssSiteConfig, head, customCssAPI: ICustomCssAPI): IAppWillMountHandler => {
		return {
			async appWillMount() {
				const cssCode = await customCssAPI.fetchStyles(siteFeatureConfig.gridAppId, GLOBAL_CSS_FILE_PATH)
				if (cssCode) {
					head.addCustomCss!(wrapInStyleElement(cssCode))
				}
			},
		}
	}
)

export const ClientAppsCustomCss = withDependencies(
	[named(PageFeatureConfigSymbol, name), named(SiteFeatureConfigSymbol, name), DomReadySymbol, CustomCssAPISymbol],
	(
		pageConfig: CustomCssPageConfig,
		siteConfig: CustomCssSiteConfig,
		domReadyPromise: Promise<void>,
		customCssAPI: ICustomCssAPI
	): IPageWillMountHandler => {
		return {
			name: 'ClientAppsCustomCss',
			pageWillMount: async () => {
				await domReadyPromise
				const cssToInject = await customCssAPI.updateAppsCustomCss(pageConfig.appsInPage, siteConfig.appsWithCustomCss)
				if (cssToInject) {
					customCssAPI.applyStyles(cssToInject)
				}
			},
		}
	}
)

export const ClientCustomCss = withDependencies(
	[named(SiteFeatureConfigSymbol, name), DomReadySymbol, CustomCssAPISymbol],
	(
		siteFeatureConfig: CustomCssSiteConfig,
		domReadyPromise: Promise<void>,
		customCssAPI: ICustomCssAPI
	): IAppWillMountHandler => {
		return {
			async appWillMount() {
				await domReadyPromise
				// dom-id-usage-check-ignore-next-line -- customCssId is the resource id of the site custom-CSS <style> element, not component identity
				const customCSSElement = document.getElementById(customCssId)
				if (customCSSElement) {
					return
				}
				const cssCode = await customCssAPI.fetchStyles(siteFeatureConfig.gridAppId, GLOBAL_CSS_FILE_PATH)
				if (cssCode) {
					const styleElement = window.document.createElement('style')
					// dom-id-usage-check-ignore-next-line -- site custom-CSS <style> resource id, not a component id
					styleElement.setAttribute('id', customCssId)
					styleElement.innerHTML = cssCode
					window.document.head.appendChild(styleElement)
				}
			},
		}
	}
)
